import { AlertCircle } from '@untitled-ui/icons-react/build/cjs';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ActionTooltip from 'src/components/ActionTooltip';
import BadgeCmp from 'src/components/BadgeCmp';
import TableCmp from 'src/components/TableCmp';
import TooltipCmp from 'src/components/TooltipCmp';
import { CLASSIFICATION_BADGE_CONFIG, EDIT_QUOTE_TABS, QUOTE_CLASSIFICATION, STATUS } from 'src/constants/common';
import { GetAppearance, getDateWithSuffixFormat, getFormattedDateIgnoringTime, getFormattedNumber, getLabelByValue, formatLinearFootage } from 'src/utils/CommonFunctions';

import QuoteDetailMetaDataLoaderRow from '../Component/QuoteDetailMetaDataLoaderRow';
import { IS_CP_PICKUP_DATE_UPDATE_ENABLED } from '../myQuote.const';

const QuoteDetailMetaDataTableRow = ({
    data,
    onOpenModal,
    onPickupDateWarningClick,
    onOpenStopDetails,
}: {
    data: any;
    onOpenModal?: (tab: string) => void;
    onPickupDateWarningClick?: () => void;
    onOpenStopDetails?: () => void;
}) => {
    const { t } = useTranslation();
    const appearance = GetAppearance();
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const [secondaryEquipmentTypes, setSecondaryEquipmentTypes] = useState<string[]>([]);

    useEffect(() => {
        if (data?.secondaryEquipmentTypeFullNames) {
            setSecondaryEquipmentTypes(data?.secondaryEquipmentTypeFullNames?.split(',').map((type: any) => type.trim()));
        } else {
            setSecondaryEquipmentTypes([]);
        }
    }, [data?.secondaryEquipmentTypeFullNames]);

    // Check if pickup date is in the past (not applicable for Booked quotes)
    const isPast = useMemo(() => {
        // Past-date alert + Update Pickup Date modal are feature-flagged off for CP — see IS_CP_PICKUP_DATE_UPDATE_ENABLED.
        if (!IS_CP_PICKUP_DATE_UPDATE_ENABLED) return false;
        if (!data?.pickupDate || data?.status === STATUS.BOOKED) return false;
        const pickupDateString = moment.utc(data?.pickupDate).format('YYYY-MM-DD');
        const todayString = moment()
            .tz(appearance?.timezone ?? browserTimezone)
            .startOf('day')
            .format('YYYY-MM-DD');

        return pickupDateString < todayString;
    }, [data?.pickupDate, appearance?.timezone, browserTimezone]);

    const classificationBadge = useMemo(() => {
        const config = CLASSIFICATION_BADGE_CONFIG[data.classification];
        if (!config) return null;

        return (
            <BadgeCmp style="modern" type={config.type} size="sm" mainClassName="bg-white">
                {t(config.label)}
            </BadgeCmp>
        );
    }, [data?.classification, t]);

    // Genuine multi-stop route — mirrors the >2 check used elsewhere on this page
    // (QuoteDetail/index.tsx, CustomerPortalStopMetaStrip.tsx): stops always includes the
    // shipper/consignee bookends, so length > 2 means at least one real middle stop survived.
    // `data.stops` (the full array) only exists once QuoteDetail's detail fetch resolves — before
    // that, `data` is the partial row the listing page handed over via router state, which carries
    // a lightweight `stopsCount` instead (see getQuotes.controller.ts) so this cell renders
    // correctly on the very first paint rather than waiting on the fetch.
    const stopsTotal = data?.stops?.length ?? Number(data?.stopsCount ?? 0);
    const isMultiStop = stopsTotal > 2;
    const intermediateStopCount = isMultiStop ? stopsTotal - 2 : 0;
    // Multi-stop quotes get an extra "Quote Type" cell (8 columns); classic quotes keep 7.
    const colWidth = isMultiStop ? 'md:w-[8.5%]' : 'md:w-[10.28%]';

    return (
        <>
            <td
                className={`${colWidth} p-3 space-y-1`}
                onClick={(e) => {
                    e.stopPropagation();
                    onOpenModal?.(EDIT_QUOTE_TABS.SHIPMENT_DETAILS);
                }}
            >
                <h6 className="table-metadata-heading">Service Type</h6>
                {classificationBadge || data?.serviceLevel === 'expedited' ? (
                    <div className="flex flex-wrap gap-1">
                        {classificationBadge}
                        {data?.serviceLevel === 'expedited' && (
                            <BadgeCmp style="modern" type="warning" size="sm" mainClassName="bg-white">
                                {t('MyQuote.Service_Level_Expedited')}
                            </BadgeCmp>
                        )}
                    </div>
                ) : (
                    <p className="text-sm leading-[24px] font-medium text-textSecondary">-</p>
                )}
            </td>
            <td
                className={`${colWidth} p-3 space-y-1 group`}
                onClick={(e) => {
                    e.stopPropagation();

                    if (isPast && onPickupDateWarningClick) {
                        onPickupDateWarningClick();
                    } else {
                        onOpenModal?.(EDIT_QUOTE_TABS.SHIPPER_DETAILS);
                    }
                }}
            >
                <h6 className="table-metadata-heading">Pickup Date</h6>
                <div className={`flex gap-1 ${isPast ? 'items-center' : ''}`}>
                    <span
                        id="pickupDate"
                        dangerouslySetInnerHTML={{
                            __html: data?.pickupDate ? getDateWithSuffixFormat(getFormattedDateIgnoringTime(data?.pickupDate, 'ddd, MMM Do, YYYY', true, true, true)) : '-',
                        }}
                        className="text-sm leading-[20px] font-medium text-textSecondary"
                    />
                    {isPast && (
                        <TooltipCmp position="top" childrenClassName="flex mt-[1px]" parentClassName="!w-max" message={t('Common.The_pickup_date_is_in_the_past')}>
                            <AlertCircle
                                className="w-4 h-4 text-fgErrorPrimary cursor-pointer"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onPickupDateWarningClick?.();
                                }}
                            />
                        </TooltipCmp>
                    )}
                </div>
            </td>
            <td
                className={`${colWidth} p-3 space-y-1 group`}
                onClick={(e) => {
                    e.stopPropagation();
                    onOpenModal?.(EDIT_QUOTE_TABS.SHIPPER_DETAILS);
                }}
            >
                <h6 className="table-metadata-heading">From</h6>
                <div className="flex gap-x-1 truncate">
                    <div className="truncate">
                        <ActionTooltip
                            message={data?.shipperFullAddress}
                            tooltipPosition="center"
                            parentClassName="max-w-[400px] w-max"
                            openOnHover
                            childrenClassName="flex"
                            isOpenChildTopPosOnTop
                            type="light"
                        >
                            <p
                                className="truncate text-sm leading-5 lg:leading-[22px] font-medium text-textSecondary"
                                onClick={() => onOpenModal?.(EDIT_QUOTE_TABS.SHIPPER_DETAILS)}
                            >{`${data?.shipperCity}, ${data?.shipperPostal}, ${data?.shipperCountryCode}`}</p>
                        </ActionTooltip>
                    </div>
                </div>
            </td>
            <td
                className={`${colWidth} p-3 space-y-1 group`}
                onClick={(e) => {
                    e.stopPropagation();
                    onOpenModal?.(EDIT_QUOTE_TABS.CONSIGNEE_DETAILS);
                }}
            >
                <h6 className="table-metadata-heading">To</h6>
                <div className="flex gap-x-1 truncate">
                    <div className="truncate">
                        <ActionTooltip
                            message={data?.consigneeFullAddress}
                            tooltipPosition="center"
                            parentClassName="max-w-[400px] w-max"
                            childrenClassName="flex"
                            openOnHover
                            isOpenChildTopPosOnTop
                            type="light"
                        >
                            <p
                                className="truncate text-sm leading-[20px] font-medium text-textSecondary"
                                onClick={() => onOpenModal?.(EDIT_QUOTE_TABS.CONSIGNEE_DETAILS)}
                            >{`${data?.consigneeCity}, ${data?.consigneePostal}, ${data?.consigneeCountryCode}`}</p>
                        </ActionTooltip>
                    </div>
                </div>
            </td>
            <td
                className={`${colWidth} p-3 space-y-1 group`}
                onClick={(e) => {
                    e.stopPropagation();
                    onOpenModal?.(data.classification === QUOTE_CLASSIFICATION.FTL ? EDIT_QUOTE_TABS.SHIPMENT_DETAILS : EDIT_QUOTE_TABS.DIMENSIONS);
                }}
            >
                <h6 className="table-metadata-heading">Total Weight</h6>
                <div className="flex gap-1 text-sm font-medium text-textSecondary">
                    <p className="truncate text-sm leading-[20px] font-medium text-textSecondary">
                        {data?.totalWeight > 0 ? getFormattedNumber(data?.totalWeight, false) : 0}{' '}
                        {data?.dimensions?.length > 0 ? getLabelByValue(data?.dimensions[0]?.weightMeasurement)?.toLowerCase() : 'lbs'}
                    </p>
                </div>
            </td>
            <td
                className={`${colWidth} p-3 space-y-1 group`}
                onClick={(e) => {
                    e.stopPropagation();
                    onOpenModal?.(data.classification === QUOTE_CLASSIFICATION.FTL ? EDIT_QUOTE_TABS.SHIPMENT_DETAILS : EDIT_QUOTE_TABS.DIMENSIONS);
                }}
            >
                <h6 className="table-metadata-heading">Linear Footage</h6>
                <div className="flex gap-1 text-sm leading-[20px] font-medium text-textSecondary">
                    {`${data?.linearFootage > 0 ? `${formatLinearFootage(data?.linearFootage)} Ft.` : data?.classification === QUOTE_CLASSIFICATION.FTL ? '53 Ft.' : '-'}`}
                </div>
            </td>
            {/* The only clickable cell on the Quote Detail page's metadata card. Opens the read-only
                Stops Summary info modal (Modals/StopDetailsModal.tsx) — NOT the Edit Quote modal,
                which this card intentionally no longer opens from any cell. */}
            {isMultiStop && (
                <td
                    className={`${colWidth} p-3 space-y-1 ${onOpenStopDetails ? 'cursor-pointer' : ''}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        onOpenStopDetails?.();
                    }}
                >
                    <h6 className="table-metadata-heading">{t('MyQuote.Quote_Type')}</h6>
                    <p className="text-sm leading-[20px] font-medium text-textSecondary">
                        {intermediateStopCount > 0 ? t('MyQuote.Multistops_Count', { count: intermediateStopCount }) : t('MyQuote.Multi_Stop')}
                    </p>
                </td>
            )}
            <td
                className={`${colWidth} p-3 space-y-1 group`}
                onClick={(e) => {
                    e.stopPropagation();
                    onOpenModal?.(EDIT_QUOTE_TABS.SHIPMENT_DETAILS);
                }}
            >
                <h6 className="table-metadata-heading">Equipment Type</h6>
                <div className="flex items-center gap-1">
                    <div className="truncate flex items-center gap-1">
                        {data?.primaryEquipmentTypeFullName ? (
                            <BadgeCmp style="modern" type="pink" size="sm" mainClassName="bg-white">
                                {data?.primaryEquipmentTypeFullName}
                            </BadgeCmp>
                        ) : !secondaryEquipmentTypes.length ? (
                            <p className="text-sm font-medium text-textSecondary">-</p>
                        ) : null}

                        {secondaryEquipmentTypes.length > 0 && (
                            <ActionTooltip
                                openOnHover
                                parentClassName="min-w-fit max-w-max w-52"
                                tooltipPosition={secondaryEquipmentTypes.length <= 2 ? 'center' : 'right'}
                                isOpenChildTopPosOnTop
                                message={secondaryEquipmentTypes.slice(0).join(', ')}
                            >
                                <BadgeCmp style="modern" type="gray" size="sm" mainClassName="bg-white" isHidePillDot>
                                    +{secondaryEquipmentTypes.length}
                                </BadgeCmp>
                            </ActionTooltip>
                        )}
                    </div>
                </div>
            </td>
        </>
    );
};

interface IProps {
    dataList: any[];
    // Optional — when omitted (Quote Detail page), the card is read-only: no cell/row click opens
    // the Edit Quote modal (client decision: quotes aren't editable after creation).
    onOpenModal?: (tab: string) => void;
    isLoading: boolean;
    onPickupDateWarningClick?: () => void;
    // Opens the read-only Stops Summary info modal from the "Quote Type" cell. Independent of
    // onOpenModal above — the card stays read-only for editing even when this is supplied.
    onOpenStopDetails?: () => void;
}

const QuoteDetailMetaDataTable = ({ dataList = [], onOpenModal, isLoading, onPickupDateWarningClick, onOpenStopDetails }: IProps) => (
    <>
        <div className="w-full rounded-xl shadow-sm border border-utilityGray200 flex justify-between flex-col bg-white max-md:p-3">
            <div className="md:overflow-x-auto custom-scrollbar scrollbar-hide table-without-pagination">
                <TableCmp
                    tableHeaderClass="!hidden"
                    tableDataArr={dataList}
                    TableRowCmp={QuoteDetailMetaDataTableRow}
                    isTableDataLoading={isLoading}
                    TableLoaderRowCmp={QuoteDetailMetaDataLoaderRow}
                    numberOfSkeletonRows={1}
                    tableBodyRowClass="max-md:!grid max-md:!grid-cols-2 max-md:gap-3 max-md:[&>td]:!h-auto max-md:[&>td]:!p-0 md:[&>td:not(:first-child):not(:last-child)]:!px-4 md:[&>td:first-child]:!pr-4 md:[&>td:first-child]:!pl-4 md:[&>td:first-child]:!py-2.5 md:[&>td:last-child]:!pl-4 md:[&>td:last-child]:!py-2.5 overflow-hidden md:[&>td:first-child]:!rounded-l-lg md:[&>td:last-child]:!rounded-r-lg"
                    tableRowCmpProps={{
                        onOpenModal: onOpenModal,
                        onPickupDateWarningClick: onPickupDateWarningClick,
                        onOpenStopDetails: onOpenStopDetails,
                    }}
                    isTableRowClickable={!!onOpenModal}
                    onRowClick={onOpenModal ? () => onOpenModal(EDIT_QUOTE_TABS.SHIPPER_DETAILS) : undefined}
                />
            </div>
        </div>
    </>
);

export default QuoteDetailMetaDataTable;
